import { Injectable } from '@angular/core';
import { Subject, Observable, config, timer } from 'rxjs';
import { HubConnectionBuilder, HubConnectionState, HubConnection, LogLevel } from "@microsoft/signalr";
import { environment } from 'src/environments/environment';
import { ClientService } from '../clients-module/services/client.service';
import { group } from '@angular/animations';

export enum ConnectionState {
  Connecting = 1,
  Connected = 2,
  Reconnecting = 3,
  Disconnected = 4
}

export class ChannelConfig {
  url: string;
  hubName: string;
  channel: string;
}

// export class ChannelEvent {
//   channelName: string;
//   // Timestamp: Date;
//   data: any;
//   groupName : string;
//   // constructor() {
//   //     this.Timestamp = new Date();
//   // }
// }

export class ChannelEvent {
    data: string;
    channelName: string;
    clientId: string;
    token: string;
    groupName: string;
    // CallbackType: PaymentEventCallbackType;
}

class ChannelSubject {
  channel: string;
  subject: Subject<ChannelEvent>;
}

/**
* ChannelService is a wrapper around the functionality that SignalR
* provides to expose the ideas of channels and events. With this service
* you can subscribe to specific channels (or groups in signalr speak) and
* use observables to react to specific events sent out on those channels.
*/
@Injectable({
  providedIn: 'root'
})
export class ChannelService {

  /**
   * starting$ is an observable available to know if the signalr 
   * connection is ready or not. On a successful connection this
   * stream will emit a value.
   */
  starting$: Observable<any>;

  /**
   * connectionState$ provides the current state of the underlying
   * connection as an observable stream.
   */
  connectionState$: Observable<void>;

  /**
   * error$ provides a stream of any error messages that occur on the 
   * SignalR connection
   */
  error$: Observable<string>;

  // These are used to feed the public observables 
  //
  private connectionStateSubject = new Subject<void>();
  private startingSubject = new Subject<any>();
  private errorSubject = new Subject<any>();
  public reconnectSubject = new Subject<any>();

  
  public get token() : string {
    return localStorage.getItem("STP-JWT");
  }
  
  // These are used to track the internal SignalR state 
  // 
  private hubConnection: HubConnection;

  // An internal array to track what channel subscriptions exist 
  //
  private subjects = new Array<ChannelSubject>();
  constructor(public clients: ClientService ) {
      // Set up our observables
      //
      this.connectionState$ = this.connectionStateSubject.asObservable();
      this.error$ = this.errorSubject.asObservable();
      this.starting$ = this.startingSubject.asObservable();
  }

  /**
   * Start the SignalR connection. The starting$ stream will emit an 
   * event if the connection is established, otherwise it will emit an
   * error.
   */
  reconnectTimer : Observable<number>;
  start(): void {
   
      this.hubConnection  = new HubConnectionBuilder().configureLogging(LogLevel.Information)
      .withUrl(environment.baseURl + "/events",{ accessTokenFactory : () => this.token })
      .withAutomaticReconnect([0, 2000, 10000, 30000, 60000, 120000])
      .build();

      this.hubConnection.onreconnected(() => {
        //proof of concept
        this.subscribeClientToSignalR();
      })

      //TODO: how would this loop play iwth the auto reconnect code above?
      this.hubConnection.onclose((err : Error) =>{
        // if(this.token){
        // this.reconnectTimer =  timer(1000, 5000);
        // const abc = this.reconnectTimer.subscribe(val => {
        //   if(this.hubConnection.state == HubConnectionState.Disconnected)
        //   {
        //     this.hubConnection.start();
        //   }else{
        //     this.reconnectSubject.next(true);
        //     abc.unsubscribe();
        //   }
        // });
        // // TODO : kick off a timer that tries to restart the connection until it is started then turns itself off?
        // this.hubConnection.start();
      // }
      });


      this.hubConnection.on("onEvent", (ev: ChannelEvent) => {

          // This method acts like a broker for incoming messages. We 
          //  check the interal array of subjects to see if one exists
          //  for the channel this came in on, and then emit the event
          //  on it. Otherwise we ignore the message.
          //
          let channelSub = this.subjects.find((x: ChannelSubject) => {
              return x.channel === ev.channelName;
          }) as ChannelSubject;

          // If we found a subject then emit the event on it
          //
          if (channelSub !== undefined) {
              return channelSub.subject.next(ev);
          }
      });

      this.hubConnection.start()
          .catch(function (err) {
              return console.error(err.toString());
          })
          .then(() => {
              this.startingSubject.next(console.log('connected to signalr server successful.'));
          });
  }

  stop(): void{
  try {
    console.warn('Logging out');
    this.hubConnection.stop().then(()=>{
      console.warn('Logged out Socket');
    });
  } catch (error) {
    
  }
  }

  /** 
   * Get an observable that will contain the data associated with a specific 
   * channel 
   * */
  sub(channel: string): Observable<ChannelEvent> {

      // Try to find an observable that we already created for the requested 
      //  channel
      //
      let channelSub = this.subjects.find((x: ChannelSubject) => {
          return x.channel === channel;
      }) as ChannelSubject;

      // If we already have one for this event, then just return it
      //
      if (channelSub !== undefined) {
          return channelSub.subject.asObservable();
      }

      channelSub = new ChannelSubject();
      channelSub.channel = channel;
      channelSub.subject = new Subject<ChannelEvent>();
      this.subjects.push(channelSub);

      return channelSub.subject.asObservable();
  }

  // Not quite sure how to handle this (if at all) since there could be
  //  more than 1 caller subscribed to an observable we created
  //
  // unsubscribe(channel: string) {
  //     this.subjects= this.subjects.filter((x: ChannelSubject) => {
  //         return x.channel === channel;
  //     });
      
  //}
  /** publish provides a way for calles to emit events on any channel. In a 
   * production app the server would ensure that only authorized clients can
   * actually emit the message, but here we're not concerned about that.
   */
  publish(ev: ChannelEvent): void {
      this.hubConnection.invoke(ev.channelName, ev);
  }

  joinGroup(groupName : string): void{
    this.hubConnection.send('JoinGroup', groupName);
  }

  publish_stp(ev: ChannelEvent): void {
    this.hubConnection.send('Send', ev);
  }

  public subscribeClientToSignalR(): void {
    this.joinGroup('client-portal')

    // then load up the connection for their clientIds
    this.clients.FetchSignalRSubs().subscribe((clientIds: number[]) => {
      clientIds.forEach((clientId: number) => {
         this.joinGroup(`${clientId}`);
        
      });
    });
  }

}